import { Card, CardBody } from 'reactstrap';
import {
   Activity,
   DollarSign,
   TrendingUp,
   Shield,
   Percent,
   CreditCard,
   Calendar,
   ArrowUpRight,
   Calculator,
   HelpCircle,
} from 'lucide-react';
import { useLoanRequest } from '../../../Hooks/LoanRequest/useLoanRequest';

export const LoanRequestInfo = ({ onClose, isModal, hideHeader }: { onClose?: () => void; isModal?: boolean; hideHeader?: boolean }) => {
   const loanRequest = useLoanRequest();

   const quotaValue = isNaN(loanRequest.totalQuota) ? 0 : loanRequest.totalQuota;
   const isFortnightly =
      loanRequest.selector.form.frequency === 'FORTNIGHTLY';

   return (
      <Card className={isModal ? "border-0 mb-0 bg-transparent w-100 shadow-none" : "loan-card border-0 mb-0"}>
         {!hideHeader && (
            <div className={isModal ? "d-flex justify-content-between align-items-center mb-3 w-100" : "card-header-custom d-flex justify-content-between align-items-center"}>
               <h5 className="mb-0 fw-bold fs-5 text-dark">Simulador de Cuotas</h5>
               {isModal && onClose && (
                  <button
                     type="button"
                     className="btn-close"
                     onClick={onClose}
                     aria-label="Close"
                  />
               )}
            </div>
         )}
         <CardBody className={isModal ? "p-0" : "card-body-custom pt-0"}>
            {/* Big Quota Hero Section */}
            <div className="quota-hero">
               <div className="quota-label">Cuota Estimada</div>
               <div className="quota-value">
                  $
                  {quotaValue.toLocaleString('es-ES', {
                     minimumFractionDigits: 2,
                     maximumFractionDigits: 2,
                  })}
               </div>
               <div className="quota-period">
                  {isFortnightly ? 'Quincenal' : 'Mensual'}
               </div>
            </div>

            {/* Detailed Breakdown */}
            <div className="breakdown-list">
               {/* Requested Amount (Net Cash to User) */}
               <div className="breakdown-item">
                  <div className="item-label-group">
                     <DollarSign size={16} />
                     <span>Monto Solicitado (Neto)</span>
                  </div>
                  <div className="item-value">
                     $
                     {(
                        Number(loanRequest.selector.form.value) || 0
                     ).toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                     })}
                  </div>
               </div>

               {/* Self-investment (5% of requested amount) */}
               <div className="breakdown-item highlight-row">
                  <div className="item-label-group">
                     <TrendingUp size={16} />
                     <span>Inversión Propia (5%)</span>
                  </div>
                  <div className="item-value">
                     $
                     {isNaN(Number(loanRequest.selector.form.value))
                        ? '0,00'
                        : (
                             Number(loanRequest.selector.form.value) * 0.05
                          ).toLocaleString('es-ES', {
                             minimumFractionDigits: 2,
                             maximumFractionDigits: 2,
                          })}
                  </div>
               </div>

               {/* Insurance Fee */}
               <div className="breakdown-item">
                  <div className="item-label-group">
                     <Shield size={16} />
                     <span>Seguro de Cartera</span>
                  </div>
                  <div className="item-value">
                     $
                     {loanRequest.totalInsurance.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                     })}
                  </div>
               </div>

               {/* Commission Fee */}
               <div className="breakdown-item">
                  <div className="item-label-group">
                     <Percent size={16} />
                     <span>Comisión Administrativa</span>
                  </div>
                  <div className="item-value">
                     $
                     {loanRequest.totalCommission.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                     })}
                  </div>
               </div>

               {/* Total Credit (Value + self-investment + insurance + commission) */}
               <div className="breakdown-item total-row">
                  <div className="item-label-group">
                     <CreditCard size={16} />
                     <span>Valor Total del Crédito</span>
                  </div>
                  <div className="item-value">
                     $
                     {Number(loanRequest.RequestedCredit).toLocaleString(
                        'es-ES',
                        {
                           minimumFractionDigits: 2,
                           maximumFractionDigits: 2,
                        }
                     )}
                  </div>
               </div>

               {/* Interest rate per period */}
               <div className="breakdown-item">
                  <div className="item-label-group">
                     <Calendar size={16} />
                     <span>Tasa del Periodo</span>
                  </div>
                  <div className="item-value">
                     {(loanRequest.periodRate * 100).toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                     })}
                     %
                  </div>
               </div>

               {/* Total interest amount */}
               <div className="breakdown-item">
                  <div className="item-label-group">
                     <ArrowUpRight size={16} />
                     <span>Intereses Generados</span>
                  </div>
                  <div className="item-value">
                     $
                     {isNaN(Number(loanRequest.selector.form.value))
                        ? '0,00'
                        : (
                             Number(loanRequest.selector.form.value) *
                             loanRequest.periodRate
                          ).toLocaleString('es-ES', {
                             minimumFractionDigits: 2,
                             maximumFractionDigits: 2,
                          })}
                  </div>
               </div>

               {/* Grand total to pay */}
               <div className="breakdown-item">
                  <div className="item-label-group">
                     <Calculator size={16} />
                     <span>Total Estimado a Pagar</span>
                  </div>
                  <div className="item-value fw-bold text-dark">
                     $
                     {isNaN(
                        Number(loanRequest.totalQuota) *
                           Number(loanRequest.selector.form.period)
                     )
                        ? '0,00'
                        : (
                             Number(loanRequest.totalQuota) *
                             Number(loanRequest.selector.form.period)
                          ).toLocaleString('es-ES', {
                             minimumFractionDigits: 2,
                             maximumFractionDigits: 2,
                          })}
                  </div>
               </div>
            </div>

            {/* Informative Banner */}
            <div className="info-alert-banner">
               <HelpCircle size={20} />
               <p>
                  La <strong>Inversión Propia (5%)</strong> es retenida como un
                  respaldo que se destina a tu propio fondo de capital social.
                  Este dinero sigue siendo tuyo y genera rendimientos a tu favor
                  dentro de la sociedad.
               </p>
            </div>
         </CardBody>
      </Card>
   );
};
